import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useAPI } from '../../../Hooks/useAPIs';
import { sanitizeMacAddress } from '../../../utils/macAddress';
import { DEVICE_TYPE_OPTIONS } from '../../../utils/deviceTypes';

const NewDeviceRequestDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [macAddress, setMacAddress] = useState('');
  const [macInputValue, setMacInputValue] = useState('');
  const [pi, setPi] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [macError, setMacError] = useState('');
  const [isNewDevice, setIsNewDevice] = useState(false);

  const { apiCall } = useAPI();

  const validateMac = (value) => {
    if (value && !/^[A-Za-z0-9]{12,}$/.test(value)) {
      return 'MAC address must be at least 12 alphanumeric characters.';
    }
    return '';
  };

  const { data: macSuggestions = [], isFetching: isSearchingMac } = useQuery({
    queryKey: ['mac-autocomplete', macInputValue],
    queryFn: async () => {
      if (!macInputValue || macInputValue.length < 3) {
        return [];
      }
      try {
        const response = await apiCall('GET', `api/v1/devices/autocomplete/?q=${encodeURIComponent(sanitizeMacAddress(macInputValue))}&field=mac_addr`);
        return response.data?.results || response.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: macInputValue.length >= 3,
    keepPreviousData: true,
    staleTime: 30000,
  });

  const [deviceFound, setDeviceFound] = React.useState(false);
  
  React.useEffect(() => {
    if (macInputValue.length >= 12 && !isSearchingMac) {
      const exactMatch = macSuggestions.some(mac => {
        const macValue = typeof mac === 'string' ? mac : (mac.mac_addr || mac);
        return macValue?.toLowerCase() === macInputValue.toLowerCase();
      });
      const isValidMac = /^[A-Za-z0-9]{12,}$/.test(macInputValue);
      setIsNewDevice(!exactMatch && isValidMac);
      setDeviceFound(exactMatch && isValidMac);
    } else {
      setIsNewDevice(false);
      setDeviceFound(false);
    }
  }, [macInputValue, macSuggestions, isSearchingMac]);

  const handleMacChange = (event, newValue) => {
    const value = typeof newValue === 'string' ? newValue : (newValue?.mac_addr || newValue || '');
    const sanitizedValue = sanitizeMacAddress(value);
    setMacAddress(sanitizedValue);
    setMacInputValue(sanitizedValue);
    setMacError(validateMac(sanitizedValue));
  };

  const handleMacInputChange = (event, newInputValue) => {
    const sanitizedValue = sanitizeMacAddress(newInputValue || '');
    setMacInputValue(sanitizedValue);
    setMacAddress(sanitizedValue);
    setMacError(validateMac(sanitizedValue));
  };

  const handleSubmit = () => {
    setError('');
    const sanitizedMacAddress = sanitizeMacAddress(macAddress);

    if (!sanitizedMacAddress) {
      setError('MAC address is required');
      return;
    }

    const nextMacError = validateMac(sanitizedMacAddress);
    if (nextMacError) {
      setMacError(nextMacError);
      setError(nextMacError);
      return;
    }

    if (isNewDevice && pi === 'other' && !deviceType) {
      setError('Please select the device type');
      return;
    }

    const requestData = {
      mac_addr: sanitizedMacAddress,
      pi: isNewDevice ? (pi || undefined) : undefined,
      device_type: isNewDevice && pi === 'other' ? deviceType : undefined,
      notes: notes.trim() || undefined,
    };

    onSubmit(requestData);
    
    setMacAddress('');
    setMacInputValue('');
    setPi('');
    setDeviceType('');
    setNotes('');
    setError('');
    setMacError('');
    setIsNewDevice(false);
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Device</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
        {macError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{macError}</Alert>}
        {isNewDevice && !macError && (
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            This device is brand new and will be registered for the first time. Please select PI and device type.
          </Alert>
        )}
        {deviceFound && !macError && (
          <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
            Device found. No need to specify device type.
          </Alert>
        )}

        <Autocomplete
          freeSolo
          options={macSuggestions}
          value={macAddress}
          inputValue={macInputValue}
          onInputChange={handleMacInputChange}
          onChange={handleMacChange}
          loading={isSearchingMac}
          disabled={isLoading}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.mac_addr || option || '';
          }}
          isOptionEqualToValue={(option, value) => {
            const optMac = typeof option === 'string' ? option : (option.mac_addr || option);
            const valMac = typeof value === 'string' ? value : (value.mac_addr || value);
            return optMac?.toLowerCase() === valMac?.toLowerCase();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="MAC Address"
              placeholder="Enter or search MAC address"
              sx={{ mb: 2, mt: 1 }}
              error={!!macError}
              helperText={macError || 'Special characters are removed automatically. Must be at least 12 alphanumeric characters'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearchingMac ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={macInputValue.length >= 3 ? "No matching MAC addresses found" : "Type at least 3 characters to search"}
        />

        {isNewDevice && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>PI</InputLabel>
              <Select
                value={pi}
                label="PI"
                onChange={(e) => {
                  setPi(e.target.value);
                  if (e.target.value !== 'other') {
                    setDeviceType('');
                  }
                }}
                disabled={isLoading}
              >
              <MenuItem value="pi-zero">PI - Zero</MenuItem>
              <MenuItem value="pi-4">PI - 4b</MenuItem>
              <MenuItem value="cm5">CM5</MenuItem>
              <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {pi === 'other' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={deviceType}
                  label="Device Type"
                  onChange={(e) => setDeviceType(e.target.value)}
                  disabled={isLoading}
                  required
                >
                  {DEVICE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        <TextField
          label="Notes (Optional)"
          fullWidth
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional notes or comments..."
          disabled={isLoading}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            isLoading ||
            !macAddress ||
            !!macError ||
            (isNewDevice && pi === 'other' && !deviceType)
          }
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewDeviceRequestDialog;

